/**
 * Sections de MasterClientDetail : Header (rename) · Coordonnées communes ·
 * UserCard (Principal/Secondaire) · EditUserModal · ConfirmArchiveModal · Stat.
 *
 * Extrait de MasterClientDetail.jsx (Batch #14.221 — refactor oversized 2026-02).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Loader2, Mail, Phone, Unlink, Link2,
  MapPin, Save, Pencil, Trash2, X, AlertTriangle, Receipt,
} from "lucide-react";
import { toast } from "@/lib/toast";
import ImpersonateButton from "@/components/admin/ImpersonateButton";

const CONTACT_METHOD_LABEL = { email: "Courriel", phone: "Téléphone", sms: "SMS" };

//  Header — nom du dossier éditable inline
// =============================================================
function MasterHeader({ master, onSaveName, busy, onDirty }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(master.couple_display_name || "");
  const [numDraft, setNumDraft] = useState(master.client_number || "");

  useEffect(() => {
    const isDirty = editing && (draft.trim() !== (master.couple_display_name || "") || numDraft.trim() !== (master.client_number || ""));
    onDirty?.(isDirty);
  }, [editing, draft, numDraft, master.couple_display_name, master.client_number, onDirty]);

  const submit = async () => {
    if (!draft.trim()) { toast.error("Nom requis"); return; }
    const ok = await onSaveName(draft.trim(), numDraft.trim());
    if (ok === false) return;
    setEditing(false);
    onDirty?.(false);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(master.couple_display_name || "");
    setNumDraft(master.client_number || "");
    onDirty?.(false);
  };

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-white border border-slate-200 rounded-2xl p-5" data-testid="masters-header">
      {!editing ? (
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800" data-testid="masters-display-name">
              {master.couple_display_name}
            </h1>
            <div className="text-[11px] text-slate-400 font-mono mt-0.5 flex items-center gap-2 flex-wrap">
              <span>{master.id}</span>
              {master.client_number
                ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 font-semibold" data-testid="masters-client-number">N° client {master.client_number}</span>
                : <button onClick={() => setEditing(true)} className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100" data-testid="masters-client-number-missing">Aucun n° de client — attribuer</button>}
            </div>
            {master.notes && <p className="text-sm text-slate-600 mt-2 italic">{master.notes}</p>}
          </div>
          <button
            onClick={() => setEditing(true)}
            className="text-xs px-2.5 py-1 rounded-lg bg-white border hover:bg-slate-50 text-slate-700 inline-flex items-center gap-1"
            data-testid="masters-name-edit-btn"
          >
            <Pencil className="w-3 h-3" /> Renommer
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Nom du dossier</label>
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                autoFocus
                className="w-full text-xl font-bold border-2 border-indigo-300 rounded-lg px-3 py-1.5 focus:outline-none focus:border-indigo-500"
                data-testid="masters-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">N° de client (comptabilité)</label>
              <input
                value={numDraft}
                onChange={(e) => setNumDraft(e.target.value.toUpperCase())}
                placeholder="Ex. 1001"
                maxLength={20}
                className="w-full text-xl font-bold font-mono border-2 border-emerald-300 rounded-lg px-3 py-1.5 focus:outline-none focus:border-emerald-500"
                data-testid="masters-client-number-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={cancel} className="px-3 py-1 text-xs rounded-lg border">Annuler</button>
            <button
              onClick={submit}
              disabled={busy}
              className="px-3 py-1 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="masters-name-save-btn"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Enregistrer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================
//  Section "Coordonnées communes" — édition inline
// =============================================================
function CommonContactSection({ master, onSave, busy, onDirty }) {
  const [editing, setEditing] = useState(false);
  const cc = useMemo(() => master.common_contact || {}, [master]);
  const [draft, setDraft] = useState({
    civic_number: "", street: "", apartment: "",
    city: "", province: "QC", postal_code: "",
    home_phone: "", preferred_contact_method: "email",
  });

  // Détecte si le draft diffère du cc d'origine (dirty)
  useEffect(() => {
    if (!editing) { onDirty?.(false); return; }
    const isDirty = Object.keys(draft).some((k) => (draft[k] || "") !== (cc[k] || ""));
    onDirty?.(isDirty);
  }, [editing, draft, cc, onDirty]);

  const startEdit = () => {
    setDraft({
      civic_number: cc.civic_number || "",
      street: cc.street || "",
      apartment: cc.apartment || "",
      city: cc.city || "",
      province: cc.province || "QC",
      postal_code: cc.postal_code || "",
      home_phone: cc.home_phone || "",
      preferred_contact_method: cc.preferred_contact_method || "email",
    });
    setEditing(true);
  };

  const submit = async () => {
    await onSave(draft);
    setEditing(false);
    onDirty?.(false);
  };

  const cancel = () => {
    setEditing(false);
    onDirty?.(false);
  };

  const fullAddress = [
    [cc.civic_number, cc.street].filter(Boolean).join(" "),
    cc.apartment ? `App. ${cc.apartment}` : null,
    cc.city, cc.province, cc.postal_code,
  ].filter(Boolean).join(", ");

  const updD = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <div className="border border-slate-200 rounded-xl p-4" data-testid="masters-common-contact">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-800 inline-flex items-center gap-2">
          <MapPin className="w-5 h-5 text-emerald-500" /> Coordonnées communes (couple)
        </h2>
        {!editing && (
          <button onClick={startEdit} disabled={busy} className="text-xs px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1" data-testid="masters-cc-edit-btn">
            <Pencil className="w-3 h-3" /> Modifier
          </button>
        )}
      </div>

      {!editing ? (
        <div className="text-sm text-slate-700 space-y-1">
          {fullAddress ? (
            <div className="inline-flex items-start gap-2">
              <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
              <span>{fullAddress}</span>
            </div>
          ) : (
            <div className="text-slate-400 italic">Aucune adresse renseignée.</div>
          )}
          {cc.home_phone && (
            <div className="inline-flex items-center gap-2 text-slate-600">
              <Phone className="w-4 h-4 text-slate-400" /> {cc.home_phone} (maison)
            </div>
          )}
          {cc.preferred_contact_method && (
            <div className="text-[11px] text-slate-500">
              Mode de contact privilégié : <b>{CONTACT_METHOD_LABEL[cc.preferred_contact_method] || cc.preferred_contact_method}</b>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <FieldEdit label="N° civique"><input className="cc-input" value={draft.civic_number} onChange={(e) => updD("civic_number", e.target.value)} data-testid="masters-cc-civic-input" /></FieldEdit>
            <FieldEdit label="Rue" wide><input className="cc-input" value={draft.street} onChange={(e) => updD("street", e.target.value)} data-testid="masters-cc-street-input" /></FieldEdit>
            <FieldEdit label="Appartement"><input className="cc-input" value={draft.apartment} onChange={(e) => updD("apartment", e.target.value)} /></FieldEdit>
            <FieldEdit label="Ville"><input className="cc-input" value={draft.city} onChange={(e) => updD("city", e.target.value)} data-testid="masters-cc-city-input" /></FieldEdit>
            <FieldEdit label="Province"><input className="cc-input" value={draft.province} onChange={(e) => updD("province", e.target.value)} maxLength={2} /></FieldEdit>
            <FieldEdit label="Code postal"><input className="cc-input" value={draft.postal_code} onChange={(e) => updD("postal_code", e.target.value)} data-testid="masters-cc-postal-input" /></FieldEdit>
            <FieldEdit label="Téléphone maison"><input className="cc-input" value={draft.home_phone} onChange={(e) => updD("home_phone", e.target.value)} /></FieldEdit>
            <FieldEdit label="Mode de contact privilégié">
              <select className="cc-input" value={draft.preferred_contact_method} onChange={(e) => updD("preferred_contact_method", e.target.value)} data-testid="masters-cc-pref-input">
                <option value="email">Courriel</option>
                <option value="phone">Téléphone</option>
                <option value="sms">SMS</option>
              </select>
            </FieldEdit>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={cancel} className="px-3 py-1.5 text-xs rounded-lg border" data-testid="masters-cc-cancel">Annuler</button>
            <button onClick={submit} disabled={busy} className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5" data-testid="masters-cc-save">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}Enregistrer
            </button>
          </div>
        </div>
      )}
      <style>{`.cc-input { width: 100%; padding: 0.5rem 0.65rem; border: 1px solid #e2e8f0; border-radius: 0.5rem; font-size: 0.875rem; }
        .cc-input:focus { outline: none; border-color: #6366f1; box-shadow: 0 0 0 1px #6366f1; }`}</style>
    </div>
  );
}

function FieldEdit({ label, wide = false, children }) {
  return (
    <label className={`block ${wide ? "sm:col-span-1" : ""}`}>
      <span className="text-[11px] text-slate-500 font-medium">{label}</span>
      {children}
    </label>
  );
}

// =============================================================
//  Batch #14.310 — Section "Facturation" : nom facturé (ex. « 9xxx-xxxx Québec inc. »)
//  Remplace les noms des membres sous « Facturé à » ; adresse / courriel optionnels (sinon ceux du dossier).
// =============================================================
const BILLING_KEYS = ["name", "address", "city", "province", "postal_code", "email"];
const emptyBilling = () => ({ name: "", address: "", city: "", province: "QC", postal_code: "", email: "" });

function BillingSection({ master, onSave, busy, onDirty }) {
  const [editing, setEditing] = useState(false);
  const b = useMemo(() => master.billing || {}, [master]);
  const [draft, setDraft] = useState(emptyBilling());
  const members = [master.primary_user, master.secondary_user].filter(Boolean).map((u) => `${u.first_name || ""} ${u.last_name || ""}`.trim()).filter(Boolean);

  useEffect(() => {
    if (!editing) { onDirty?.(false); return; }
    onDirty?.(BILLING_KEYS.some((k) => (draft[k] || "") !== (b[k] || (k === "province" ? "QC" : ""))));
  }, [editing, draft, b, onDirty]);

  const startEdit = () => { setDraft({ ...emptyBilling(), ...Object.fromEntries(BILLING_KEYS.filter((k) => b[k]).map((k) => [k, b[k]])) }); setEditing(true); };
  const submit = async () => {
    const ok = await onSave(draft.name.trim() ? draft : Object.fromEntries(BILLING_KEYS.map((k) => [k, ""])));
    if (ok === false) return;
    setEditing(false); onDirty?.(false);
  };
  const cancel = () => { setEditing(false); onDirty?.(false); };
  const updD = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const billAddr = [b.address, [b.city, [b.province, b.postal_code].filter(Boolean).join(" ")].filter(Boolean).join(", ")].filter(Boolean).join(", ");

  return (
    <div className="border border-slate-200 rounded-xl p-4" data-testid="masters-billing">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-800 inline-flex items-center gap-2">
          <Receipt className="w-5 h-5 text-amber-500" /> Facturation (nom facturé)
        </h2>
        {!editing && (
          <button onClick={startEdit} disabled={busy} className="text-xs px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1" data-testid="masters-billing-edit-btn">
            <Pencil className="w-3 h-3" /> {b.name ? "Modifier" : "Définir"}
          </button>
        )}
      </div>
      {!editing ? (
        b.name ? (
          <div className="text-sm text-slate-700 space-y-1">
            <div className="font-semibold text-slate-900" data-testid="masters-billing-name">{b.name}</div>
            {members.length > 0 && <div className="text-slate-600" data-testid="masters-billing-attention">À l'attention de : {members.join(" et ")}</div>}
            <div className="inline-flex items-start gap-2 text-slate-600"><MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" /><span data-testid="masters-billing-address">{billAddr || "Adresse commune du dossier"}</span></div>
            <div className="inline-flex items-center gap-2 text-slate-600"><Mail className="w-4 h-4 text-slate-400" /><span data-testid="masters-billing-email">{b.email || "Courriel du membre principal"}</span></div>
            <p className="text-[11px] text-slate-500 pt-1">Les factures du cabinet sont adressées à ce nom ; les membres apparaissent en « À l'attention de ».</p>
          </div>
        ) : (
          <div className="text-sm text-slate-400 italic" data-testid="masters-billing-empty">Aucun nom facturé — les factures sont adressées aux membres du dossier ({members.join(" et ") || "—"}).</div>
        )
      ) : (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block sm:col-span-3"><span className="text-[11px] text-slate-500 font-medium">Nom facturé (raison sociale) — vide pour retirer</span>
              <input className="cc-input" value={draft.name} onChange={(e) => updD("name", e.target.value)} placeholder="Ex. 9xxx-xxxx Québec inc." maxLength={160} autoFocus data-testid="masters-billing-name-input" /></label>
            <FieldEdit label="Adresse de facturation (optionnel)" wide><input className="cc-input" value={draft.address} onChange={(e) => updD("address", e.target.value)} placeholder="Sinon : adresse commune" data-testid="masters-billing-address-input" /></FieldEdit>
            <FieldEdit label="Ville"><input className="cc-input" value={draft.city} onChange={(e) => updD("city", e.target.value)} data-testid="masters-billing-city-input" /></FieldEdit>
            <FieldEdit label="Province / Code postal"><div className="flex gap-2"><input className="cc-input w-16" value={draft.province} onChange={(e) => updD("province", e.target.value.toUpperCase())} maxLength={2} /><input className="cc-input" value={draft.postal_code} onChange={(e) => updD("postal_code", e.target.value.toUpperCase())} placeholder="H0H 0H0" data-testid="masters-billing-postal-input" /></div></FieldEdit>
            <FieldEdit label="Courriel de facturation (optionnel)" wide><input className="cc-input" type="email" value={draft.email} onChange={(e) => updD("email", e.target.value)} placeholder="Sinon : courriel du membre principal" data-testid="masters-billing-email-input" /></FieldEdit>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={cancel} className="px-3 py-1.5 text-xs rounded-lg border" data-testid="masters-billing-cancel">Annuler</button>
            <button onClick={submit} disabled={busy} className="px-3 py-1.5 text-xs rounded-lg bg-amber-600 text-white hover:bg-amber-700 inline-flex items-center gap-1.5" data-testid="masters-billing-save">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}Enregistrer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================
//  UserCard — Principal/Secondaire avec Modifier/Supprimer/Délier
// =============================================================
function UserCard({ user, role, badge, onEdit, onDelete, onUnlink, busy }) {
  if (!user) return null;
  return (
    <div
      className="border border-slate-200 rounded-lg p-3 bg-white"
      data-testid={`masters-user-card-${role.toLowerCase()}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-600 flex-shrink-0">
          {(user.first_name?.[0] || "").toUpperCase()}{(user.last_name?.[0] || "").toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800 truncate">{user.first_name} {user.last_name}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${badge}`}>{role}</span>
          </div>
          <div className="text-[11px] text-slate-500 space-y-0.5 mt-1">
            {user.email && <div className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {user.email}</div>}
            {user.phone && <div className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {user.phone}</div>}
            {user.portal_username && <div className="inline-flex items-center gap-1 text-emerald-600"><Link2 className="w-3 h-3" /> portail actif ({user.portal_username})</div>}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-slate-100 flex-wrap">
        {(user.portal_enabled || user.portal_username) && (
          <ImpersonateButton client={{ ...user, portal_enabled: user.portal_enabled ?? !!user.portal_username }} compact
            className="text-[11px] px-2 py-1 rounded-md hover:bg-red-50 text-slate-600 hover:text-red-700 disabled:opacity-40 inline-flex items-center gap-1" />
        )}
        <button
          onClick={onEdit}
          disabled={busy}
          className="text-[11px] px-2 py-1 rounded-md hover:bg-blue-50 text-slate-600 hover:text-blue-600 disabled:opacity-50 inline-flex items-center gap-1"
          data-testid={`masters-user-${role.toLowerCase()}-edit`}
        >
          <Pencil className="w-3 h-3" /> Modifier
        </button>
        {onUnlink && (
          <button
            onClick={onUnlink}
            disabled={busy}
            className="text-[11px] px-2 py-1 rounded-md hover:bg-amber-50 text-slate-600 hover:text-amber-700 disabled:opacity-50 inline-flex items-center gap-1"
            data-testid={`masters-user-${role.toLowerCase()}-unlink`}
          >
            <Unlink className="w-3 h-3" /> Délier
          </button>
        )}
        <button
          onClick={onDelete}
          disabled={busy}
          className="text-[11px] px-2 py-1 rounded-md hover:bg-red-50 text-slate-600 hover:text-red-600 disabled:opacity-50 inline-flex items-center gap-1"
          data-testid={`masters-user-${role.toLowerCase()}-delete`}
        >
          <Trash2 className="w-3 h-3" /> Supprimer
        </button>
      </div>
    </div>
  );
}

// =============================================================
//  Modale d'édition d'un membre (Principal ou Secondaire)
// =============================================================
function EditUserModal({ user, role, onClose, onSave, busy }) {
  const [form, setForm] = useState({
    first_name: user.first_name || "",
    last_name: user.last_name || "",
    email: user.email || "",
    phone: user.phone || "",
  });

  const submit = () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("Prénom et nom requis");
      return;
    }
    onSave({
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="masters-edit-user-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">Modifier le membre {role}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="masters-edit-user-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="text-xs text-slate-500 font-medium">Prénom *</span>
            <input className="w-full mt-0.5 px-3 py-2 border rounded-lg" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="masters-edit-user-first" />
          </label>
          <label className="block">
            <span className="text-xs text-slate-500 font-medium">Nom *</span>
            <input className="w-full mt-0.5 px-3 py-2 border rounded-lg" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="masters-edit-user-last" />
          </label>
          <label className="block">
            <span className="text-xs text-slate-500 font-medium">Courriel</span>
            <input type="email" className="w-full mt-0.5 px-3 py-2 border rounded-lg" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="masters-edit-user-email" />
          </label>
          <label className="block">
            <span className="text-xs text-slate-500 font-medium">Téléphone (mobile)</span>
            <input className="w-full mt-0.5 px-3 py-2 border rounded-lg" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="masters-edit-user-phone" />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Annuler</button>
          <button onClick={submit} disabled={busy} className="px-4 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="masters-edit-user-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================
//  Modale de confirmation d'archivage Master
// =============================================================
function ConfirmArchiveModal({ masterName, onClose, onConfirm, busy }) {
  const [confirmText, setConfirmText] = useState("");
  const expected = "ARCHIVER";
  const canConfirm = confirmText.trim().toUpperCase() === expected;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="masters-archive-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-slate-800">Archiver ce dossier Master ?</h3>
            <p className="text-xs text-slate-500 mt-1">
              Le dossier <b>{masterName}</b> sera archivé (récupérable depuis la corbeille).
              Les clients liés ne sont PAS supprimés automatiquement — utilisez les boutons "Supprimer" sur chaque membre si nécessaire.
            </p>
          </div>
        </div>
        <label className="block text-xs text-slate-700 mt-3">
          Tapez <b className="font-mono text-rose-600">{expected}</b> pour confirmer :
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="w-full mt-1 px-3 py-2 border rounded-lg text-sm font-mono uppercase"
            data-testid="masters-archive-confirm-input"
            autoFocus
          />
        </label>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border" data-testid="masters-archive-cancel">Annuler</button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm || busy}
            className="px-4 py-1.5 text-sm rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-30 inline-flex items-center gap-1.5"
            data-testid="masters-archive-confirm"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Archiver
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const tones = { emerald: "text-emerald-700", indigo: "text-indigo-700" };
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`text-xl font-bold mt-0.5 ${tones[tone] || "text-slate-800"}`}>{value}</div>
    </div>
  );
}

export { MasterHeader, CommonContactSection, BillingSection, UserCard, EditUserModal, ConfirmArchiveModal, Stat };
